import { ColDef, SuppressKeyboardEventParams } from "ag-grid-community";
import DraftLegActionHeader from "../cellRenderers/DraftLeg/DraftLegActionHeader";
import {
  BlockEditCell,
  DraftLegDirectionCell,
  DraftLegDiscardCell,
  DraftLegExternalIdCell,
  DraftLegIsinCell,
  DraftLegRiskCell,
  DraftLegSaveCell,
  DraftLegSecurityCell,
  DraftLegSettlementCell,
  DraftLegSizeCell,
  DraftLegStatusCell,
} from "../cellRenderers/DraftLeg/DraftLegCells";
import { isDraftLegRow } from "../cellRenderers/DraftLeg/useDraftLegTicket";
import { returnDraftLegLockedTooltip } from "../cellRenderers/DraftLeg/draftLegLockHelpers";

// LQC-2941 - draft leg rows share the Trade Creation grid with real blocks. Mapped
// columns swap to an inline editor on a draft row; every other column shows nothing
// and nothing on a draft row is AG Grid editable or selectable.

const DRAFT_RENDERERS: Record<string, unknown> = {
  CreationDirectionCell: DraftLegDirectionCell,
  CreationNotionalCell: DraftLegSizeCell,
  CreationSecurityCell: DraftLegSecurityCell,
  CreationISINCell: DraftLegIsinCell,
  CreationSettlementCell: DraftLegSettlementCell,
  CreationExternalIDCell: DraftLegExternalIdCell,
  CreationDV01Cell: DraftLegRiskCell,
  CreationStatus: DraftLegStatusCell,
};

const NULL_RENDERER = () => null;

// LQC-3027 - columns a FIX block's edit can lock; they explain why on hover.
const LOCKABLE_COL_IDS = ["CreationDirectionCell", "CreationSecurityCell", "CreationExternalIDCell"];

// The draft's inputs own their keys: AG Grid would otherwise take Enter / Tab / arrows.
function suppressDraftKeys(params: SuppressKeyboardEventParams): boolean {
  return isDraftLegRow(params.node?.data);
}

function wrapColDef(colDef: ColDef & Record<string, any>): ColDef {
  const draftRenderer = colDef.colId ? DRAFT_RENDERERS[colDef.colId] : undefined;
  const blockRenderer = colDef.cellRenderer;
  const blockEditable = colDef.editable;
  const blockCheckbox = colDef.checkboxSelection;
  const isGroupColumn = blockRenderer === "agGroupCellRenderer";

  return {
    ...colDef,
    // The expand arrow stays on drafts: it opens the allocation / Security Details row.
    ...(isGroupColumn
      ? {}
      : {
          cellRendererSelector: (params: any) => {
            if (!isDraftLegRow(params.data)) {
              return blockRenderer ? { component: blockRenderer } : undefined;
            }
            return { component: draftRenderer ?? NULL_RENDERER };
          },
          cellRenderer: undefined,
        }),
    checkboxSelection: blockCheckbox
      ? (params: any) => !isDraftLegRow(params.data)
      : blockCheckbox,
    editable: (params: any) => {
      if (isDraftLegRow(params.data)) return false;
      return typeof blockEditable === "function"
        ? blockEditable(params)
        : !!blockEditable;
    },
    suppressKeyboardEvent: suppressDraftKeys,
    ...(colDef.colId && LOCKABLE_COL_IDS.includes(colDef.colId)
      ? {
          tooltipValueGetter: (params: any) =>
            returnDraftLegLockedTooltip(colDef.colId!, params.data),
        }
      : {}),
  };
}

export function wrapColDefsForDraftLegs(colDefs: ColDef[]): ColDef[] {
  return colDefs.map((colDef) => wrapColDef(colDef as ColDef & Record<string, any>));
}

interface IDraftLegActionColumn {
  colId: string;
  width: number;
  // Named in the columns list and the heading's tooltip; the heading itself is an icon.
  headerName: string;
  // The same glyph as the column's cells; none leaves the heading blank.
  headerIcon?: string;
  component: unknown;
  blockComponent?: unknown;
  // Shares the Save column's heading, so one entry in the columns list covers both.
  hideFromColumnsList?: boolean;
}

// Fixed, one action each: the Order fills pattern (Add / Edit columns). Blank on block
// rows unless blockComponent is set. Locked visible: hiding Save would strand a draft.
function returnDraftLegActionColDef(
  { colId, width, headerName, headerIcon, component, blockComponent, hideFromColumnsList }: IDraftLegActionColumn,
  widgetId?: string,
): ColDef & Record<string, any> {
  return {
    colId,
    headerName,
    headerTooltip: headerName,
    headerComponent: DraftLegActionHeader,
    headerComponentParams: { icon: headerIcon, testId: `${colId}Header` },
    width,
    fixedWidth: true,
    resizable: false,
    suppressMovable: true,
    lockVisible: true,
    suppressColumnsToolPanel: hideFromColumnsList,
    sortable: false,
    filter: false,
    enableRowGroup: false,
    cellClass: "reducedPaddingLeft reducedPaddingRight",
    headerClass: `fixedWidthHeader ${colId}Header`,
    cellRendererSelector: (params: any) => {
      if (isDraftLegRow(params.data)) return { component };
      return blockComponent ? { component: blockComponent, params: { widgetId } } : { component: NULL_RENDERER };
    },
    suppressKeyboardEvent: suppressDraftKeys,
  };
}

// LQC-3027 - the Save column also holds a saved block's Edit pencil.
export function returnDraftLegActionColDefs(widgetId?: string): ColDef[] {
  return [
    returnDraftLegActionColDef(
      {
        colId: "CreationDraftLegSave",
        width: 46,
        headerName: "Edit Leg",
        headerIcon: "✎",
        component: DraftLegSaveCell,
        blockComponent: BlockEditCell,
      },
      widgetId,
    ),
    returnDraftLegActionColDef({
      colId: "CreationDraftLegDiscard",
      width: 22,
      headerName: "Discard leg / changes",
      component: DraftLegDiscardCell,
      hideFromColumnsList: true,
    }),
  ];
}

export const DRAFT_LEG_ACTION_COL_DEFS: ColDef[] = returnDraftLegActionColDefs();

const FILLER_COL_ID = "filler";

// Straight after the data columns (before the filler), like the fills' action columns.
export function insertDraftLegActionColDefs(colDefs: ColDef[], widgetId?: string): ColDef[] {
  const fillerIndex = colDefs.findIndex((colDef) => colDef.colId === FILLER_COL_ID);
  const insertAt = fillerIndex === -1 ? colDefs.length : fillerIndex;
  return [
    ...colDefs.slice(0, insertAt),
    ...returnDraftLegActionColDefs(widgetId),
    ...colDefs.slice(insertAt),
  ];
}
